import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Chip,
  Modal,
  CircularProgress,
} from "@mui/material";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FaCheck } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { VendorProfile } from "../../../Redux/action/VendorProfileAction";

const FreePlan = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.VendorProfile?.result);

  const PLAN_KEYS = {
    Monthly: "MONTHLY",
    Quarterly: "QUARTERLY",
    "Half Yearly": "HALF_YEARLY",
    Yearly: "YEARLY",
  };

  const [activeTab, setActiveTab] = useState("MONTHLY");
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeLoader, setUpgradeLoader] = useState(null);

  const [cancelModal, setCancelModal] = useState(false);
  const [needRender, setNeedRender] = useState(false);

  // Reference for active plan auto scroll
  const activeCardRef = useRef(null);

  /** Fetch user profile */
  useEffect(() => {
    dispatch(VendorProfile());
  }, [needRender]);

  /** When user info loads, set toggle based on subscribed duration */
  useEffect(() => {
    if (!userInfo) return;
    const duration = userInfo?.userSubscription?.subscriptionPlan?.duration;
    setActiveTab(duration || "MONTHLY");
  }, [userInfo]);

  /** Fetch all plans when tab changes */
  useEffect(() => {
    if (!activeTab) return;
    fetchPlans();
  }, [activeTab]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_PATH}/subscription_plan/get_subscription_plan_by_user_type?userType=VENDOR&duration=${activeTab}`
      );
      setDetails(res?.data?.data || []);
    } catch {
      toast.error("Failed to fetch subscription plans.");
    } finally {
      setTimeout(() => setLoading(false), 400); // smooth UI hold
    }
  };

  /** Auto scroll to active subscribed card ONCE data loads */
  useEffect(() => {
    if (!loading && activeCardRef.current) {
      activeCardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [loading]);

  // Handle toggle tabs
  const handleTabChange = (key) => {
    if (key === activeTab) return;
    setLoading(true);
    setTimeout(() => {
      setActiveTab(key);
    }, 150);
  };

  const handleBuyPlan = async (planId) => {
    setUpgradeLoader(planId);

    try {
      const orderResponse = await axios.post(
        `${process.env.REACT_APP_API_PATH}/payment/create_order`,
        {
          subscriptionPlanId: planId,
          userId: userInfo.vendorId,
          userType: "VENDOR",
        }
      );

      if (!orderResponse?.data?.status?.success) {
        toast.error("Failed to create order.");
        setUpgradeLoader(null);
        return;
      }

      const { razorpayOrderId, amount } = orderResponse.data.data;

      const Razorpay = new window.Razorpay({
        key: "rzp_test_6RDAELPDeFpXXx",
        amount: amount * 100,
        currency: "INR",
        name: "Ebench",
        order_id: razorpayOrderId,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.put(
              `${process.env.REACT_APP_API_PATH}/payment/verify_payment`,
              {
                paymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id,
                razorpaySignature: response.razorpay_signature,
              }
            );

            if (verifyResponse?.data?.status?.success) {
              toast.success("Subscription updated!");
              setNeedRender((prev) => !prev);
            } else {
              toast.error("Payment verification failed.");
            }
          } finally {
            setUpgradeLoader(null);
          }
        },
      });

      Razorpay.open();
    } catch {
      toast.error("Payment process failed.");
      setUpgradeLoader(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_PATH}/user_subscription/cancel_subscription?userId=${userInfo.vendorId}`
      );

      if (res?.data?.status?.success) {
        toast.success("Subscription cancelled.");
        setCancelModal(false);
        setNeedRender((prev) => !prev);
      } else toast.error("Cancellation failed.");
    } catch {
      toast.error("Something went wrong.");
    }
  };

  const currentPlanId =
    userInfo?.userSubscription?.subscriptionPlan?.subscriptionId;

  return (
    <Box sx={{ px: 2, py: 4, maxWidth: "1200px", mx: "auto" }}>
      <ToastContainer />

      {/* Title */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.2,
            px: 2.5,
            py: 1,
            borderRadius: 3,
            background: "#E8F4FF",
            color: "#0E3F6F",
            fontSize: "14px",
            fontWeight: 600,
            mb: 2,
            border: "1px solid #cfe8ff",
          }}
        >
          🚀 Exclusive Access
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#0E3F6F",
            letterSpacing: "-0.5px",
            fontSize: { xs: "26px", md: "32px" },
          }}
        >
          Upgrade Your Hiring Experience
        </Typography>

        <Typography
          sx={{
            maxWidth: "680px",
            mx: "auto",
            mt: 1.5,
            fontSize: "15.5px",
            color: "#475569",
            lineHeight: "1.6",
          }}
        >
          Choose a plan crafted for growth — unlock advanced recruiting tools,
          AI-based talent matching, and premium support.
          <strong>No long-term contracts. Cancel anytime.</strong>
        </Typography>

        {/* ⭐ Small Feature Highlights */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 2,
            color: "#0E3F6F",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            ⚡ Fast Activation
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            🔄 Upgrade or Downgrade Anytime
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            💳 Secure Payments
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            🛎 Premium Support
          </Box>
        </Box>
      </Box>

      {/* Toggle Duration */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Box
          sx={{
            display: "inline-flex",
            background: "#e9eff5",
            p: 1.2,
            borderRadius: "30px",
          }}
        >
          {Object.entries(PLAN_KEYS).map(([label, key]) => (
            <Button
              key={key}
              onClick={() => handleTabChange(key)}
              sx={{
                px: 3,
                py: 1.1,
                borderRadius: "20px",
                background: activeTab === key ? "#0E3F6F" : "transparent",
                color: activeTab === key ? "#fff" : "#475569",
                "&:hover": {
                  background: activeTab === key ? "#0C355B" : "#d9e2ee",
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Plan Cards */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress size={50} />
            </Box>
          </Grid>
        ) : (
          details.map((plan) => {
            const isCurrent = currentPlanId === plan.subscriptionId;

            return (
              <Grid item xs={12} sm={6} lg={4} key={plan.subscriptionId}>
                <Card
                  ref={isCurrent ? activeCardRef : null}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    border: isCurrent
                      ? "2px solid #059669"
                      : "1px solid #dcdcdc",
                    background: isCurrent ? "#ecfdf5" : "#fff",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-3px)" },
                  }}
                >
                  <CardContent>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6">
                        {plan.subscriptionType}
                      </Typography>
                      {isCurrent && (
                        <Chip
                          icon={<IoMdCheckmarkCircleOutline />}
                          label="Current"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Price */}
                    <Typography variant="h4">
                      ₹{plan.discountedPrice}
                      <Typography
                        component="span"
                        sx={{ fontSize: 14, color: "#6b7280" }}
                      >
                        /{plan.duration?.toLowerCase()}
                      </Typography>
                    </Typography>

                    {/* Features */}
                    <Box sx={{ mt: 2 }}>
                      {plan?.featureList?.map((feature, i) => (
                        <Typography
                          key={i}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FaCheck size={14} color="green" /> {feature}
                        </Typography>
                      ))}
                    </Box>

                    {/* Button */}
                    <Box textAlign="right" sx={{ mt: 3 }}>
                      {isCurrent ? (
                        plan.subscriptionType !== "FREE" && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setCancelModal(true)}
                          >
                            Cancel Subscription
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="contained"
                          style={{ backgroundColor: "#286a94", borderRadius: 8 }}
                          onClick={() => handleBuyPlan(plan.subscriptionId)}
                          disabled={upgradeLoader === plan.subscriptionId}
                        >
                          {upgradeLoader === plan.subscriptionId ? (
                            <CircularProgress
                              size={18}
                              sx={{ color: "#fff" }}
                            />
                          ) : (
                            "Subscribe"
                          )}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Cancel Modal */}
      <Modal open={cancelModal} onClose={() => setCancelModal(false)}>
        <Box
          sx={{
            background: "#fff",
            width: 380,
            p: 4,
            mx: "auto",
            mt: "22vh",
            borderRadius: 3,
            textAlign: "center",
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
          }}
        >
          <Typography variant="h6">Cancel Subscription?</Typography>
          <Typography sx={{ mt: 1, mb: 3, color: "#6b7280" }}>
            You can re-subscribe anytime.
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setCancelModal(false)}
            >
              Close
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleCancelSubscription}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default FreePlan;
